import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Paper, LinearProgress, Button } from "@material-ui/core";
import { FaBook, FaCheck, FaRocket } from "react-icons/fa";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import useGettingStarted from "../../hooks/useGettingStarted";
import { askDigi, stepVideo, videoUrl } from "../../hooks/useGettingStarted/sellTrack";
import api from "../../services/api";
import { TINT, RADII, BORDERS } from "../../theme/redesign";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(1, 1, 4, 0.5),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2, 3, 5, 1),
    },
  },
  hello: {
    fontSize: "1.75rem",
    fontWeight: 800,
    letterSpacing: -0.4,
    marginBottom: theme.spacing(2.5),
    color: theme.palette.text.primary,
  },
  setupCard: {
    borderRadius: 18,
    border: BORDERS.base(theme),
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2.5, 2.5, 1.5),
    marginBottom: theme.spacing(3),
  },
  setupHead: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 14,
    marginBottom: theme.spacing(2),
  },
  manualButton: {
    marginLeft: "auto",
    flexShrink: 0,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    whiteSpace: "nowrap",
  },
  rocket: {
    width: 48,
    height: 48,
    borderRadius: 14,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    fontSize: 26,
  },
  setupTitle: {
    fontWeight: 800,
    fontSize: "1.15rem",
    lineHeight: 1.2,
  },
  setupSub: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: 2,
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 8,
  },
  nextLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    "& strong": {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
  count: {
    fontSize: 13,
    fontWeight: 700,
    color: "#16a34a",
    whiteSpace: "nowrap",
  },
  bar: {
    height: 7,
    borderRadius: 999,
    backgroundColor: theme.mode === "dark" ? "#1f2937" : "#e8f5e9",
    marginBottom: theme.spacing(1.5),
    "& .MuiLinearProgress-bar": {
      borderRadius: 999,
      backgroundColor: "#22c55e",
    },
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 14,
    width: "100%",
    padding: "14px 6px",
    border: "none",
    borderTop: BORDERS.base(theme),
    borderRadius: 10,
    background: "none",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.08)" : "#f8fafc",
    },
  },
  stepDone: {
    cursor: "pointer",
    "& $stepTitle, & $stepDesc": {
      color: theme.palette.text.disabled,
      textDecoration: "line-through",
      textDecorationThickness: 1,
    },
  },
  bubble: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: `2px solid ${theme.mode === "dark" ? "#475569" : "#d1d5db"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  bubbleDone: {
    border: "none",
    backgroundColor: "#2563eb",
    color: "#fff",
  },
  stepIcon: {
    color: theme.palette.text.secondary,
    fontSize: 22,
    flexShrink: 0,
  },
  stepBody: { flex: 1, minWidth: 150 },
  stepTitle: {
    fontWeight: 700,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  optional: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.2,
    color: theme.palette.primary.main,
    backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.16)" : "#eef2ff",
    borderRadius: 999,
    padding: "2px 8px",
  },
  stepDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  chevron: {
    color: theme.palette.text.disabled,
    fontSize: 14,
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    margin: theme.spacing(2.5, 0, 1),
  },
  extrasTitle: {
    fontWeight: 800,
    fontSize: "1.2rem",
    marginBottom: 4,
  },
  extrasSub: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontSize: 14,
  },
  extrasGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  extraCard: {
    borderRadius: RADII.surface,
    border: BORDERS.base(theme),
    boxShadow: "none",
    padding: theme.spacing(2),
    cursor: "pointer",
    backgroundColor: TINT.brand(theme),
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    minWidth: 0,
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
    },
  },
  extraWide: {
    [theme.breakpoints.up("sm")]: {
      gridColumn: "1 / -1",
    },
  },
  extraIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { fontSize: 22 },
  },
  guide: {
    fontSize: 13, lineHeight: 1.6, marginTop: 10,
    "& summary": { cursor: "pointer", color: theme.palette.primary.main, fontWeight: 600 },
    "& ol": { paddingLeft: 22 },
    "& li": { marginBottom: 8 },
  },
  extraTitle: {
    fontWeight: 800,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  extraDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.4,
  },
  tracks: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  trackButton: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 999,
  },
  stepMissing: {
    display: "block",
    fontSize: 12.5,
    color: "#b45309",
    marginTop: 4,
    fontWeight: 600,
  },
  stepActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  stepAction: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12.5,
    borderRadius: 999,
    padding: "2px 10px",
  },
  newBadge: {
    fontSize: 11,
    fontWeight: 800,
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    borderRadius: 999,
    padding: "2px 8px",
  },
}));

const GettingStarted = () => {
  const classes = useStyles();
  const history = useHistory();
  const { loading, planError, refresh, steps, done, total, nextStep, userName, track, canChooseTrack, setTrack } = useGettingStarted();
  const [helps, setHelps] = useState([]);
  useEffect(() => {
    api.get("/helps/list").then(({ data }) => setHelps(Array.isArray(data) ? data : [])).catch(() => setHelps([]));
  }, []);
  const titleOf = (step) => step.title || i18n.t(step.titleKey);
  const descOf = (step) => step.desc || i18n.t(step.descKey);
  const selling = canChooseTrack && track === "sell";

  return (
    <MainContainer>
      <div className={classes.root}>
        <Typography className={classes.hello}>
          {i18n.t("gettingStarted.hello", { name: userName })}
        </Typography>

        {steps.length === 0 && (
          <Button
            className={classes.manualButton}
            style={{ marginBottom: 16 }}
            variant="outlined"
            color="primary"
            component="a"
            href="/manual/Manual-Watoolx.pdf"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<FaBook size={14} />}
          >
            {i18n.t("gettingStarted.manual")}
          </Button>
        )}
        {loading && <Typography role="status">{i18n.t("gettingStarted.loading")}</Typography>}
        {planError && <Paper style={{ padding: 16 }}>
          <Typography role="alert">{i18n.t("gettingStarted.planError")}</Typography>
          <Button color="primary" onClick={refresh}>{i18n.t("gettingStarted.retry")}</Button>
        </Paper>}
        {canChooseTrack && (
          <div className={classes.tracks} role="group" aria-label="Tipo de empresa">
            <Button className={classes.trackButton} variant={selling ? "contained" : "outlined"} color="primary" onClick={() => setTrack("sell")} aria-pressed={selling}>
              🛒 Vender servicios digitales
            </Button>
            <Button className={classes.trackButton} variant={!selling ? "contained" : "outlined"} color="primary" onClick={() => setTrack("attention")} aria-pressed={!selling}>
              💬 Atención y difusión
            </Button>
          </div>
        )}
        {steps.length > 0 && (
          <Paper className={classes.setupCard} elevation={0}>
            <div className={classes.setupHead}>
              <div className={classes.rocket}>
                <FaRocket />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <Typography className={classes.setupTitle}>
                  {i18n.t("gettingStarted.title")}
                </Typography>
                <Typography className={classes.setupSub}>
                  {selling
                    ? "Arma tu tienda de servicios digitales en orden: conexión, pagos, productos, stock, bot, agente y tienda web. Cada paso se marca solo con tus datos reales."
                    : i18n.t("gettingStarted.subtitle")}
                </Typography>
              </div>
              <Button
                className={classes.manualButton}
                variant="outlined"
                color="primary"
                component="a"
                href="/manual/Manual-Watoolx.pdf"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<FaBook size={14} />}
              >
                {i18n.t("gettingStarted.manual")}
              </Button>
            </div>
            <div className={classes.progressRow}>
              <Typography className={classes.nextLabel}>
                {nextStep
                  ? (
                    <>
                      {i18n.t("gettingStarted.next")}{" "}
                      <strong>{titleOf(nextStep)}</strong>
                    </>
                  )
                  : i18n.t("gettingStarted.allDone")}
              </Typography>
              <span className={classes.count}>
                {loading ? "…" : i18n.t("gettingStarted.completed", { done, total })}
              </span>
            </div>
            <LinearProgress
              className={classes.bar}
              variant="determinate"
              value={total ? (done / total) * 100 : 0}
            />
            {steps.map((step, index) => {
              const video = stepVideo(helps, step.id);
              const href = video ? videoUrl(video) : null;
              const go = () => history.push(step.to);
              return (
              <div
                key={step.id}
                role="button"
                tabIndex={0}
                className={`${classes.step} ${step.done ? classes.stepDone : ""}`}
                onClick={go}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); go(); } }}
              >
                <span className={`${classes.bubble} ${step.done ? classes.bubbleDone : ""}`}>
                  {step.done ? <FaCheck size={12} /> : index + 1}
                </span>
                <span className={classes.stepBody}>
                  <span className={classes.stepTitle}>
                    {titleOf(step)}
                    {step.optional ? <span className={classes.optional}>{i18n.t("gettingStarted.optional")}</span> : null}
                  </span>
                  <span className={classes.stepDesc}>{descOf(step)}</span>
                  {!step.done && step.missing ? <span className={classes.stepMissing}>Falta: {step.missing}</span> : null}
                  <span className={classes.stepActions}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      className={classes.stepAction}
                      onClick={(e) => { e.stopPropagation(); askDigi(step.digi || `Guíame paso a paso: ${titleOf(step)}`); }}
                    >
                      🧭 Guíame con Digi
                    </Button>
                    {href ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.stepAction}
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📺 Ver video
                      </Button>
                    ) : null}
                  </span>
                </span>
              </div>
              );
            })}
          </Paper>
        )}

        <Typography className={classes.setupSub} style={{ marginTop: 16 }}>
          {i18n.t("gettingStarted.guidesInModules")}
        </Typography>
      </div>
    </MainContainer>
  );
};

export default GettingStarted;
